import { effectiveCpmRateCents } from '@/lib/campaigns/cpmOverrideOptions';
import { CampaignReportError, type ReportCampaign, type ReportSnapshot, type ReportSubmission } from './types';

export const REPORT_TIMEZONE = 'America/Sao_Paulo';
export const PLATFORM_LABELS = { youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok' } as const;
type Platform = keyof typeof PLATFORM_LABELS;
export type ReportPoint = { date: string; value: number };

export function safeInteger(value: unknown, signed = false): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || (!signed && value < 0)) {
    throw new CampaignReportError('invalid');
  }
  return value;
}
function views(value: unknown): number {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) throw new CampaignReportError('invalid');
  return safeInteger(Number(value));
}
function finiteTotal(value: number): number {
  if (!Number.isFinite(value) || value < 0 || value > Number.MAX_SAFE_INTEGER) throw new CampaignReportError('invalid');
  return value;
}
function requiredText(value: unknown): asserts value is string {
  if (typeof value !== 'string' || !value.trim()) throw new CampaignReportError('invalid');
}
export function reportTimestamp(value: string): number {
  requiredText(value);
  const day = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isFinite(Date.parse(day))
    || new Date(`${day}T12:00:00Z`).toISOString().slice(0, 10) !== day
    || (value.length !== 10 && !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value))) {
    throw new CampaignReportError('invalid');
  }
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) throw new CampaignReportError('invalid');
  return timestamp;
}
export function reportDay(value: string): string {
  const timestamp = reportTimestamp(value);
  if (value.length === 10) return value;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(timestamp);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export function formatReportDate(value: string | null | undefined, time = false, seconds = false): string {
  if (!value) return 'Não disponível';
  const day = reportDay(value);
  const label = day.split('-').reverse().join('/');
  return time && value.length !== 10
    ? `${label} ${new Intl.DateTimeFormat('pt-BR', { timeZone: REPORT_TIMEZONE, hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' as const } : {}), hourCycle: 'h23' }).format(reportTimestamp(value))}`
    : label;
}

/** Links remain public social-video destinations; do not export tracking data. */
export function safeVideoUrl(value: string | null | undefined, platform: string): string | null {
  if (!value || value.length > 4096) return null;
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return null;
    const host = url.hostname.toLowerCase();
    let videoId: string | null = null;
    let valid = false;
    if (platform === 'youtube') {
      if (['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(host)) {
        videoId = url.pathname === '/watch' ? url.searchParams.get('v') : null;
        valid = videoId ? /^[\w-]+$/.test(videoId) : /^\/(shorts|live|embed)\/[\w-]+\/?$/.test(url.pathname);
      } else valid = host === 'youtu.be' && /^\/[\w-]+\/?$/.test(url.pathname);
    }
    if (platform === 'instagram') valid = ['instagram.com', 'www.instagram.com'].includes(host)
      && /^\/(reel|reels|p|tv)\/[\w-]+\/?$/.test(url.pathname);
    if (platform === 'tiktok') valid = (['tiktok.com', 'www.tiktok.com', 'm.tiktok.com'].includes(host)
      && /^\/@[\w.-]+\/(video|photo)\/\d+\/?$/.test(url.pathname))
      || (['vm.tiktok.com', 'vt.tiktok.com'].includes(host) && /^\/[\w-]+\/?$/.test(url.pathname));
    if (!valid) return null;
    url.protocol = 'https:';
    url.hash = '';
    url.search = '';
    if (videoId) url.searchParams.set('v', videoId);
    return url.toString();
  } catch { return null; }
}

export function validateCampaign(campaign: ReportCampaign): void {
  if (!campaign) throw new CampaignReportError('invalid');
  for (const key of ['id', 'name', 'status', 'currency'] as const) requiredText(campaign[key]);
  if (!/^[A-Z]{3}$/.test(campaign.currency)) throw new CampaignReportError('invalid');
  for (const key of ['cpm_rate_cents', 'budget_total_cents', 'budget_spent_cents', 'budget_stored_spent_cents', 'budget_retained_cents', 'budget_remaining_cents', 'submissions_count'] as const) safeInteger(campaign[key]);
  safeInteger(campaign.budget_spend_drift_cents, true);
  for (const key of ['min_views_threshold', 'max_payout_per_clip_cents', 'max_payout_per_clipper_cents', 'view_accrual_window_hours'] as const) {
    if (campaign[key] != null) safeInteger(campaign[key]);
  }
  reportTimestamp(campaign.createdAt);
  reportTimestamp(campaign.updatedAt);
  if (campaign.start_date != null) reportTimestamp(campaign.start_date);
  if (campaign.end_date != null) reportTimestamp(campaign.end_date);
}

function validateSubmission(row: ReportSubmission): void {
  if (!row) throw new CampaignReportError('invalid');
  for (const key of ['id', 'user_id', 'platform', 'url'] as const) requiredText(row[key]);
  if (!Object.prototype.hasOwnProperty.call(PLATFORM_LABELS, row.platform)
    || !['approved', 'neutral', 'disapproved'].includes(row.admin_approval)
    || typeof row.disqualified !== 'boolean' || typeof row.suspected_duplicate !== 'boolean'
    || !Array.isArray(row.daily_views)) throw new CampaignReportError('invalid');
  views(row.last_counted_views);
  safeInteger(row.accumulated_payout_cents);
  if (row.cpm_rate_override_cents != null) safeInteger(row.cpm_rate_override_cents);
  if (row.final_accrual_views != null) views(row.final_accrual_views);
  reportTimestamp(row.createdAt);
  for (const key of ['last_view_sync_at', 'accrual_window_ends_at', 'final_accrual_captured_at'] as const) {
    if (row[key] != null) reportTimestamp(row[key]);
  }
  if (row.final_accrual_captured_at && row.final_accrual_views == null) throw new CampaignReportError('invalid');
  for (const point of row.daily_views) {
    if (!point) throw new CampaignReportError('invalid');
    reportDay(point.date);
    views(point.views);
  }
}

function monday(day: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  return date.toISOString().slice(0, 10);
}
function weeklySeries(rows: ReportSubmission[]): ReportPoint[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const week = monday(reportDay(row.createdAt));
    counts.set(week, (counts.get(week) || 0) + 1);
  }
  const dates = [...counts.keys()].sort();
  if (!dates.length) return [];
  const result: ReportPoint[] = [];
  const date = new Date(`${dates[0]}T12:00:00Z`);
  while (date.toISOString().slice(0, 10) <= dates[dates.length - 1]) {
    const day = date.toISOString().slice(0, 10);
    result.push({ date: day, value: counts.get(day) || 0 });
    date.setUTCDate(date.getUTCDate() + 7);
  }
  return result;
}
function historySeries(rows: ReportSubmission[]): ReportPoint[] {
  const deltas = new Map<string, number>();
  for (const row of rows) {
    const byDate = new Map<string, number>();
    for (const point of row.daily_views) {
      const day = reportDay(point.date);
      byDate.set(day, Math.max(byDate.get(day) || 0, views(point.views)));
    }
    let highest = 0;
    for (const [date, count] of [...byDate].sort(([a], [b]) => a.localeCompare(b))) {
      const next = Math.max(highest, count);
      deltas.set(date, safeInteger((deltas.get(date) || 0) + (next - highest)));
      highest = next;
    }
  }
  let total = 0;
  return [...deltas].sort(([a], [b]) => a.localeCompare(b)).map(([date, delta]) => {
    total = safeInteger(total + delta);
    return { date, value: total };
  });
}

export function buildCampaignReport(snapshot: ReportSnapshot) {
  const { campaign, submissions, collectionStartedAt, collectionFinishedAt } = snapshot;
  validateCampaign(campaign);
  const finished = reportTimestamp(collectionFinishedAt);
  if (reportTimestamp(collectionStartedAt) > finished || !Array.isArray(submissions)) throw new CampaignReportError('invalid');
  const seen = new Set<string>();
  for (const row of submissions) {
    validateSubmission(row);
    if (row.accumulated_payout_cents > 0 && effectiveCpmRateCents(campaign, row.cpm_rate_override_cents) === 0) {
      throw new CampaignReportError('invalid');
    }
    if (seen.has(row.id)) throw new CampaignReportError('inconsistent');
    seen.add(row.id);
  }
  const approved = submissions.filter((row) => row.admin_approval === 'approved' && !row.disqualified);
  const review = { approved: approved.length, pending: 0, rejected: 0, disqualified: 0, total: submissions.length };
  for (const row of submissions) {
    if (row.disqualified) review.disqualified++;
    else if (row.admin_approval === 'neutral') review.pending++;
    else if (row.admin_approval === 'disapproved') review.rejected++;
  }
  const platforms = (Object.keys(PLATFORM_LABELS) as Platform[]).map((platform) => ({ platform,
    label: PLATFORM_LABELS[platform], count: 0, views: 0, creditCents: 0, equivalentViews: 0 }));
  const accrual = { final: 0, open: 0, expired: 0 };
  let suspectedDuplicates = 0;
  let recentCaptures = 0;
  let missingCaptures = 0;
  let latestCapture: string | null = null;
  let oldestCapture: string | null = null;
  const windowHours = campaign.view_accrual_window_hours ?? 72;
  const videos = approved.map((row) => {
    const rate = effectiveCpmRateCents(campaign, row.cpm_rate_override_cents);
    if (rate === 0 && row.accumulated_payout_cents > 0) throw new CampaignReportError('invalid');
    const numerator = BigInt(row.accumulated_payout_cents) * 1000n;
    const equivalentViews = rate === 0 ? 0 : finiteTotal(Number(numerator / BigInt(rate)) + Number(numerator % BigInt(rate)) / rate);
    const count = views(row.last_counted_views);
    const platform = platforms.find((entry) => entry.platform === row.platform)!;
    platform.count++;
    platform.views = safeInteger(platform.views + count);
    platform.creditCents = safeInteger(platform.creditCents + row.accumulated_payout_cents);
    platform.equivalentViews = finiteTotal(platform.equivalentViews + equivalentViews);
    let state: keyof typeof accrual = 'open';
    if (row.final_accrual_captured_at != null && row.final_accrual_views != null) state = 'final';
    else if (row.accrual_window_ends_at || windowHours !== 0) {
      const end = row.accrual_window_ends_at ? reportTimestamp(row.accrual_window_ends_at)
        : reportTimestamp(row.createdAt) + windowHours * 3600000;
      if (!Number.isSafeInteger(end) || !Number.isFinite(new Date(end).getTime())) throw new CampaignReportError('invalid');
      if (end < finished) state = 'expired';
    }
    accrual[state]++;
    if (row.suspected_duplicate) suspectedDuplicates++;
    if (row.last_view_sync_at) {
      const captured = reportTimestamp(row.last_view_sync_at);
      if (captured <= finished && captured >= finished - 48 * 3600000) recentCaptures++;
      if (!latestCapture || captured > reportTimestamp(latestCapture)) latestCapture = row.last_view_sync_at;
      if (!oldestCapture || captured < reportTimestamp(oldestCapture)) oldestCapture = row.last_view_sync_at;
    } else missingCaptures++;
    return { platform: PLATFORM_LABELS[row.platform as Platform],
      url: safeVideoUrl(row.canonical_url, row.platform) || safeVideoUrl(row.url, row.platform),
      views: count, creditCents: row.accumulated_payout_cents, equivalentViews,
      lastSync: row.last_view_sync_at, accrual: state };
  });
  const totals = { views: 0, creditCents: 0, equivalentViews: 0, approved: approved.length,
    creators: new Set(approved.map((row) => row.user_id)).size };
  for (const platform of platforms) {
    totals.views = safeInteger(totals.views + platform.views);
    totals.creditCents = safeInteger(totals.creditCents + platform.creditCents);
    totals.equivalentViews = finiteTotal(totals.equivalentViews + platform.equivalentViews);
  }
  // Money kept from disqualified-account clips still leaves the budget: the
  // backend's effective spend is approved credits plus that retained money.
  const effectiveSpentCents = safeInteger(totals.creditCents + campaign.budget_retained_cents);
  const reservedCents = Math.max(campaign.budget_stored_spent_cents, effectiveSpentCents);
  const remainingCents = Math.max(0, campaign.budget_total_cents - reservedCents);
  if (campaign.submissions_count !== submissions.length || campaign.budget_spent_cents !== effectiveSpentCents
    || campaign.budget_spend_drift_cents !== campaign.budget_stored_spent_cents - effectiveSpentCents
    || campaign.budget_remaining_cents !== remainingCents) throw new CampaignReportError('inconsistent');
  return {
    campaign: { ...campaign }, collectionStartedAt, collectionFinishedAt, totals, review, platforms,
    accrual, windowHours, suspectedDuplicates, recentCaptures, missingCaptures, latestCapture, oldestCapture,
    remainingCents, reservedCents,
    utilizationPercent: campaign.budget_total_cents ? totals.creditCents / campaign.budget_total_cents * 100 : 0,
    effectiveCpm: totals.views ? totals.creditCents / totals.views * 10 : null,
    history: historySeries(approved), weekly: weeklySeries(submissions),
    topVideos: videos.sort((a, b) => b.views - a.views || b.creditCents - a.creditCents).slice(0, 10),
  };
}
export type CampaignReport = ReturnType<typeof buildCampaignReport>;
